// The greenhouse in section: a long cut through a run of glasshouse rooms, each with its own roof and its own topic.
// Feet, x along the building, y up from the ground line. Roofs are cross-gables, so each room reads as a gable in the cut.
import type { Kit, SectionDef } from './section';

const PROFILE: number[][] = [
  [0, 15, 34, 15], // entry: low and solid
  [34, 22, 61, 32, 88, 22], // exhibition: a tall nave
  [88, 24, 111, 41, 134, 24], // courtyard: the tallest, for the tree
  [134, 20, 184, 30], // shop: a shed roof rising to the right
  [184, 19, 209, 28, 234, 19], // furniture
  [234, 19, 246, 19, 246, 23, 264, 23, 264, 19, 276, 19], // lookbook, with a clerestory
];
const BOUNDS = [0, 34, 88, 134, 184, 234, 276];
const NAMES: [string, string][] = [
  ['Entry', 'Entry'], ['Exhibition', 'Exhibition'], ['Clock tree', 'Clock tree'],
  ['Shop', 'Shop'], ['Furniture', 'Furniture'], ['Lookbook', 'Lookbook'],
];

function roofAt(x: number): number {
  for (const p of PROFILE) {
    if (x < p[0] || x > p[p.length - 2]) continue;
    for (let i = 0; i + 3 < p.length; i += 2) {
      if (x >= p[i] && x <= p[i + 2]) return p[i + 2] === p[i] ? Math.max(p[i + 1], p[i + 3]) : p[i + 1] + ((p[i + 3] - p[i + 1]) * (x - p[i])) / (p[i + 2] - p[i]);
    }
  }
  return 20;
}

function draw(k: Kit): void {
  const c = k.c;
  const lo = (-k.ox) / k.s, hi = (k.w - k.ox) / k.s; // visible world span, feet
  const vis = (a: number, b: number): boolean => b > lo - 4 && a < hi + 4;
  const W = BOUNDS[BOUNDS.length - 1];

  // grass beyond both ends, swaying a little
  {
    const g = k.ctx;
    g.strokeStyle = c.tuft; g.lineWidth = k.lw(0.7); g.lineCap = 'round';
    g.beginPath();
    const a = Math.max(Math.floor(lo / 2.4) * 2.4, -90), b = Math.min(hi, W + 90);
    for (let x = a; x <= b; x += 2.4) {
      if (x > -2 && x < W + 2) continue;
      for (let j = 0; j < 3; j++) {
        const hgt = 1.8 + ((Math.sin(x * 12.9 + j * 3.1) + 1) * 1.5);
        const sway = Math.sin(k.t * 1.3 + x * 0.4 + j) * 0.5 + (j - 1) * 0.5;
        g.moveTo(k.X(x + j * 0.4), k.Y(0)); g.lineTo(k.X(x + j * 0.4 + sway), k.Y(hgt));
      }
    }
    g.stroke();
  }

  // steps up to the door
  k.poly([-9, 0, -9, 0.5, -6, 0.5, -6, 1, -3, 1, -3, 1.5, 0, 1.5, 0, 0], c.paper, 1);

  // rooms: interior, contents, roof
  for (let r = 0; r < 6; r++) {
    const x0 = BOUNDS[r], x1 = BOUNDS[r + 1];
    if (!vis(x0, x1)) continue;
    const pr = PROFILE[r];
    const shell: number[] = [x0, 0];
    for (let i = 0; i < pr.length; i += 2) shell.push(pr[i], pr[i + 1]);
    shell.push(x1, 0);
    k.poly(shell, r === 0 ? c.paper : c.interior, 0);
    if (r > 0) { // glazing bars on the back wall
      const g = k.ctx;
      g.strokeStyle = c.inkSoft; g.lineWidth = k.lw(0.4); g.beginPath();
      for (let x = x0 + 4; x < x1 - 1; x += 6) { g.moveTo(k.X(x), k.Y(0)); g.lineTo(k.X(x), k.Y(roofAt(x) - 0.4)); }
      for (const y of [9, 17]) { g.moveTo(k.X(x0), k.Y(y)); g.lineTo(k.X(x1), k.Y(y)); }
      g.stroke();
    }
    [entry, exhibition, courtyard, shop, furniture, lookbook][r](k);
    // the roof: a thin cut band with glazing bars below it
    for (let i = 0; i + 3 < pr.length; i += 2) {
      const ax = pr[i], ay = pr[i + 1], bx = pr[i + 2], by = pr[i + 3];
      if (ax === bx) { k.line([ax, ay, bx, by], 0.8); continue; }
      const t = r === 0 ? 1.2 : 0.55;
      k.poly([ax, ay, bx, by, bx, by - t, ax, ay - t], c.poche, 0);
      if (r > 0) {
        const g = k.ctx;
        g.strokeStyle = c.ink; g.lineWidth = k.lw(0.35); g.beginPath();
        const sl = (by - ay) / (bx - ax);
        for (let x = ax + 3; x < bx - 0.5; x += 6) { const y = ay + sl * (x - ax); g.moveTo(k.X(x), k.Y(y - t)); g.lineTo(k.X(x), k.Y(y - t - 1.8)); }
        g.stroke();
      }
    }
  }

  // the cut: floor slab and footings, posts where the rooms meet (a lintel over each doorway), end walls
  k.rect(-1, -1.4, W + 1, 0, c.poche, 0);
  for (let i = 0; i < BOUNDS.length; i++) {
    const x = BOUNDS[i];
    if (!vis(x, x)) continue;
    k.rect(x - 2.2, -3.2, x + 2.2, -1.4, c.poche, 0);
    const top = i === 0 ? 16.2 : i === BOUNDS.length - 1 ? 19.6 : Math.max(roofAt(x - 0.01), roofAt(x + 0.01));
    if (i === BOUNDS.length - 1) k.rect(x - 0.45, 0, x + 0.45, top, c.poche, 0);
    else k.rect(x - 0.45, 10.4, x + 0.45, top, c.poche, 0);
  }
  k.rect(-1, 15, 0.5, 16.2, c.poche, 0);
  k.rect(-0.5, 15, 34.5, 16.2, c.poche, 0);

  // below the line: widths and room names, which also work as places to tap
  {
    const px = Math.max(9, Math.min(15, k.s * 1.15));
    const y = k.gy + Math.max(16, k.s * 2);
    const g = k.ctx;
    g.strokeStyle = c.ink; g.lineWidth = 1; g.beginPath();
    g.moveTo(k.X(0), y); g.lineTo(k.X(W), y);
    for (const x of BOUNDS) { g.moveTo(k.X(x), y - 5); g.lineTo(k.X(x), y + 5); }
    g.stroke();
    for (let r = 0; r < 6; r++) {
      if (!vis(BOUNDS[r], BOUNDS[r + 1])) continue;
      const cx = k.X((BOUNDS[r] + BOUNDS[r + 1]) / 2);
      k.text(`${BOUNDS[r + 1] - BOUNDS[r]} ft`, cx, y - 8, px * 0.85, 'center');
      k.text(NAMES[r][0].toUpperCase(), cx, y + px + 12, px, 'center');
    }
  }
}

// ---- the rooms ----------------------------------------------------------------
function entry(k: Kit): void {
  const c = k.c;
  k.rect(6, 0, 15, 1.7, c.paper, 1); // a bench
  k.rect(6.8, 0, 7.6, 1.7, c.poche, 0); k.rect(13.4, 0, 14.2, 1.7, c.poche, 0);
  for (const x of [8, 12]) k.line([x, 7.5, x, 6.2], 1); // coat hooks
  k.circle(8, 6.1, 0.45, c.ink, 0); k.circle(12, 6.1, 0.45, c.ink, 0);
  // the key: a plate on the wall with a keyhole, and a bell
  k.rect(20, 5.6, 27, 11.4, c.paperShade, 1.2);
  k.circle(23.5, 9, 1.5, c.lime, 1.2);
  k.circle(23.5, 9.3, 0.4, c.poche, 0); k.rect(23.3, 7.6, 23.7, 9.2, c.poche, 0);
  k.circle(23.5, 6.7, 0.35, c.ink, 0);
  k.person(29.5, -1);
}

function exhibition(k: Kit): void {
  const c = k.c;
  const works: [number, number, number, number, number][] = [[40, 8, 6, 8, 0], [49, 9, 10, 7, 1], [62, 7, 5, 5, 2], [70, 9, 7, 9, 3], [80, 10, 4, 6, 4]];
  for (const [x, y, w, h, kind] of works) {
    k.rect(x, y, x + w, y + h, c.paper, 1.2);
    const ix = x + 0.7, iy = y + 0.7, ex = x + w - 0.7, ey = y + h - 0.7;
    if (kind === 0) k.rect(ix, iy, ex, ey, c.coral, 0);
    else if (kind === 1) { k.rect(ix, iy, ix + (ex - ix) * 0.55, ey, c.cobalt, 0); k.line([ix + (ex - ix) * 0.55, iy, ix + (ex - ix) * 0.55, ey], 0.6); }
    else if (kind === 2) k.circle((ix + ex) / 2, (iy + ey) / 2, Math.min(ex - ix, ey - iy) / 2, c.yellow, 0.7);
    else if (kind === 3) { for (let i = 0; i < 5; i++) k.line([ix, iy + ((ey - iy) * (i + 0.5)) / 5, ex, iy + ((ey - iy) * (i + 0.5)) / 5], 0.9); }
    else { k.poly([ix, iy, ex, iy, (ix + ex) / 2, ey], c.lime, 0.7); }
    k.line([x + w / 2, y + h, x + w / 2, 21], 0.4, c.inkSoft);
  }
  k.line([38, 21, 84, 21], 0.8); // a lighting track
  for (const x of [45, 55, 65, 75]) k.poly([x - 0.6, 21, x + 0.6, 21, x + 0.35, 19.9, x - 0.35, 19.9], c.poche, 0);
  k.rect(45, 0, 49.4, 3.2, c.paper, 1.2); k.circle(47.2, 4.4, 1.2, c.lime, 1);
  k.rect(64.5, 0, 68.9, 3.2, c.paper, 1.2); k.rect(65.4, 3.2, 68, 5.8, c.cobalt, 1);
  k.person(56, 1); k.person(77, -1);
}

function courtyard(k: Kit): void {
  const c = k.c;
  const cx = 111;
  for (const [a, b] of [[92, 100], [122, 130]]) {
    k.rect(a, 0, b, 2.4, c.paper, 1.2);
    k.rect(a + 0.3, 2, b - 0.3, 2.4, c.poche, 0);
    for (let x = a + 1.3; x < b - 0.5; x += 2.2) {
      const h = 1.8 + ((Math.sin(x * 3.7) + 1) * 1.6);
      k.line([x, 2.4, x, 2.4 + h], 0.8);
      k.circle(x, 2.4 + h + 0.6, 1.0 + (Math.sin(x) + 1) * 0.35, c.leaf, 0.8);
    }
  }
  k.rect(103.5, 0, 118.5, 1.5, c.paper, 1.2); // a bench round the tree
  // the tree
  for (const [x, y, r] of [[111, 30, 8.4], [103, 26.5, 5.4], [119, 26.5, 5.4], [107, 35, 5], [115, 35, 5]]) k.circle(x, y, r, c.leaf, 0.9);
  k.poly([cx - 1.3, 1.5, cx + 1.3, 1.5, cx + 0.8, 20, cx - 0.8, 20], c.poche, 0);
  for (const [x, y] of [[103, 24], [119, 25], [111, 28], [106.5, 29], [115.5, 29]]) k.line([cx, 15 + (y - 24) * 0.2, x, y], 1.3);
  // the clock on the trunk: it shows the owner's age
  const cy = 10.5;
  k.circle(cx, cy, 3.5, c.paper, 1.4);
  const g = k.ctx;
  g.strokeStyle = c.ink; g.lineWidth = k.lw(0.5); g.beginPath();
  for (let i = 0; i < 12; i++) { const a = (i / 12) * Math.PI * 2; g.moveTo(k.X(cx + 2.8 * Math.sin(a)), k.Y(cy + 2.8 * Math.cos(a))); g.lineTo(k.X(cx + 3.2 * Math.sin(a)), k.Y(cy + 3.2 * Math.cos(a))); }
  g.stroke();
  const am = k.t * 0.15, ah = am / 12 + 1.1;
  k.line([cx, cy, cx + 2.9 * Math.sin(am), cy + 2.9 * Math.cos(am)], 0.8);
  k.line([cx, cy, cx + 1.9 * Math.sin(ah), cy + 1.9 * Math.cos(ah)], 1.3);
  k.circle(cx, cy, 0.3, c.coral, 0);
  for (const x of [97, 125]) k.line([x, roofAt(x) - 0.6, x, 28], 0.5, c.inkSoft); // hanging plants
  for (const x of [97, 125]) k.circle(x, 27, 1.3, c.leaf, 0.7);
}

function shop(k: Kit): void {
  const c = k.c;
  const fills = [c.lime, c.coral, c.cobalt, c.yellow, c.paper];
  k.line([138, 14, 158, 14], 1.2); // a rail on brackets
  k.rect(137.4, 12.6, 138.6, 14.4, c.paper, 0.8); k.rect(157.4, 12.6, 158.6, 14.4, c.paper, 0.8);
  for (let i = 0; i < 10; i++) {
    const x = 139.6 + i * 1.85;
    k.poly([x, 14, x - 0.9, 13, x + 0.9, 13], null, 0.6);
    k.rect(x - 0.8, 7.8, x + 0.8, 13, fills[i % 5], 0.7);
  }
  k.rect(140, 0, 153, 3.6, c.paper, 1.2); k.rect(139.5, 3.6, 153.5, 4.1, c.poche, 0); // a counter
  k.rect(143, 4.1, 146, 5.6, c.cobalt, 0.8); k.rect(148, 4.1, 150.4, 5.2, c.paper, 0.8);
  k.rect(159, 0, 183, 2.2, c.paper, 1.2); // a bench of plants, tended by two machines in branded apparel
  for (let x = 160.6; x < 182; x += 2.7) { k.line([x, 2.2, x, 3.2 + ((Math.sin(x * 2.3) + 1) * 0.9)], 0.8); k.circle(x, 3.9 + ((Math.sin(x * 2.3) + 1) * 0.9), 1.15, c.leaf, 0.8); }
  for (const x of [165, 176.5]) {
    k.circle(x - 1.3, 0.55, 0.55, c.paper, 0.9); k.circle(x + 1.3, 0.55, 0.55, c.paper, 0.9);
    k.rect(x - 1.9, 1.1, x + 1.9, 2.5, c.paper, 1);
    k.rect(x - 1.3, 2.5, x + 1.3, 6.9, c.lime, 1); // the apparel
    k.circle(x, 5, 0.45, c.poche, 0);
    k.rect(x - 0.3, 6.9, x + 0.3, 7.5, c.poche, 0);
    k.circle(x, 8.4, 1, c.paper, 1); k.circle(x + 0.35, 8.5, 0.28, c.poche, 0);
    k.line([x + 1.3, 5.6, x + 3, 4.4, x + 3.1, 3.3], 1.4);
  }
}

function furniture(k: Kit): void {
  const c = k.c;
  const tag = (x: number, y: number, ax: number, ay: number): void => { // an NFT tag on a thread
    k.line([ax, ay, x + 2, y], 0.4, c.inkSoft);
    k.rect(x, y, x + 4, y + 2.6, c.paper, 0.8);
    k.line([x + 0.7, y + 0.7, x + 3.3, y + 1.9], 0.6);
    k.circle(x + 3, y + 0.8, 0.35, c.coral, 0);
  };
  // sofa
  k.rect(188.5, 1.2, 201.5, 2.8, c.coral, 1); k.rect(188.5, 2.8, 201.5, 6.2, c.coral, 1);
  k.rect(188, 1.2, 189.4, 4.3, c.coral, 1); k.rect(200.6, 1.2, 202, 4.3, c.coral, 1);
  k.line([189.5, 1.2, 189.5, 0], 1); k.line([200.5, 1.2, 200.5, 0], 1);
  // armchair
  k.rect(206, 1.2, 213, 2.6, c.cobalt, 1); k.rect(206, 2.6, 207.6, 6.6, c.cobalt, 1); k.rect(206, 2.6, 213, 3.2, c.cobalt, 0.8);
  k.line([206.6, 1.2, 206.6, 0], 1); k.line([212.4, 1.2, 212.4, 0], 1);
  // side table and lamp
  k.rect(215, 2.2, 219, 2.7, c.poche, 0); k.line([215.6, 2.2, 215.6, 0], 1); k.line([218.4, 2.2, 218.4, 0], 1);
  k.line([217, 2.7, 217, 5.8], 0.8); k.poly([215.7, 5.8, 218.3, 5.8, 217.7, 7.6, 216.3, 7.6], c.yellow, 0.8);
  // a bench
  k.rect(222, 1.4, 232, 2.1, c.paper, 1); k.rect(222.8, 0, 223.6, 1.4, c.poche, 0); k.rect(230.4, 0, 231.2, 1.4, c.poche, 0);
  for (const x of [195, 210]) { k.line([x, roofAt(x) - 0.6, x, 16], 0.5); k.circle(x, 15.2, 1.2, c.yellow, 0.9); }
  tag(192.6, 9.4, 195, 6.2); tag(207.4, 9.4, 209.5, 6.6); tag(225, 5, 227, 2.1);
}

function lookbook(k: Kit): void {
  const c = k.c;
  const fills = [c.coral, c.paper, c.cobalt, c.lime, c.yellow, c.paper];
  for (const x of [238, 250, 262]) k.line([x, 0, x, 18.2], 1.2);
  for (const y of [0.3, 4.6, 9.2, 13.8, 18.2]) k.line([238, y, 262, y], 1);
  for (let row = 0; row < 4; row++) {
    const y0 = [0.3, 4.6, 9.2, 13.8][row];
    let x = 238.6;
    for (let i = 0; x < 261.2; i++) {
      const w = 0.7 + ((Math.sin(row * 9 + i * 4.1) + 1) * 0.4), h = 2.3 + ((Math.sin(row * 5 + i * 2.3) + 1) * 0.8);
      if (Math.sin(row * 3 + i * 1.7) > 0.8) { x += 1.4; continue; }
      k.rect(x, y0 + 0.12, Math.min(x + w, 261.6), y0 + 0.12 + h, fills[(i + row) % 6], 0.6);
      x += w + 0.08;
    }
  }
  k.rect(240, 2.9, 251, 3.5, c.poche, 0); k.line([241, 2.9, 241, 0], 1); k.line([250, 2.9, 250, 0], 1);
  k.poly([243, 3.5, 247, 3.5, 247, 4.3, 245, 3.9, 243, 4.3], c.paper, 0.7);
  k.rect(238.3, 1.4, 240, 1.9, c.cobalt, 0.8); k.rect(238.3, 1.9, 238.9, 4.6, c.cobalt, 0.8);
  k.line([239, 1.4, 239, 0], 0.8);
  k.rect(266, 0, 272, 4.4, c.paper, 1.2); k.rect(267, 4.4, 271, 7.6, c.coral, 0.9); k.circle(269, 6, 0.9, c.paper, 0.7);
  k.rect(273.2, 0, 274.2, 6.4, c.poche, 0); k.rect(271.8, 6.4, 275.6, 8.8, c.lime, 1); k.line([272.6, 7.9, 274.8, 7.9], 0.8);
  k.person(258.4, -1);
}

export const greenhouseSection: SectionDef = {
  id: 'greenhouse',
  width: 276,
  height: 44,
  rooms: NAMES.map(([name, heading], i) => ({ name, heading, x0: BOUNDS[i], x1: BOUNDS[i + 1] })),
  draw,
};
